import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, delay, map, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface RegistrationInvitation {
  id: string;
  email: string;
  givenName: string;
  familyName: string;
  invitationLink: string;
  createdAt: string;
  expiresAt: string;
  status: 'PENDING' | 'COMPLETED' | 'EXPIRED';
}

export interface ProfessionalRegistrationStatus {
  hasCompletedPreRegistration: boolean;
  professionalId?: string;
  currentStep?: 'PORTAL_ACCESS' | 'PENDING_DATA_INPUT' | 'PENDING_VERIFICATION' | 'PENDING_CONTRACT' | 'PENDING_SIGNATURE';
  preRegistrationData?: any;
}

@Injectable({
  providedIn: 'root',
})
export class RegistrationInvitationService {
  private http = inject(HttpClient);
  //private apiUrl = `${environment.coreApiUrl}/pal-dmn-core/api/v1/ciam/users/invite`;
  private apiUrl = `${environment.coreApiUrl}/api/v1/ciam/users/invite`;
  
  // Mock storage for invitations
  private mockInvitations: Map<string, RegistrationInvitation> = new Map();
  
  // Mock storage for registration status (key: email)
  private mockRegistrationStatus: Map<string, ProfessionalRegistrationStatus> = new Map();

  /**
   * Create and send invitation (unified API call)
   * POST /pal-dmn-core/api/v1/ciam/users/invite
   * The API creates the invitation AND sends the email in one operation
   * Returns the invitation object with the generated link
   */
  createAndSendInvitation(
    email: string,
    givenName: string,
    familyName: string
  ): Observable<RegistrationInvitation> {
    const payload = {
      email,
      givenName,
      familyName
    };

    console.log('[API] POST', this.apiUrl);
    console.log('[API] Payload:', payload);
    
    return this.http.post<any>(this.apiUrl, payload).pipe(
      map(response => {
        console.log('[API] Response:', response);
        
        // API returns: { status: true, message: "...", data: { email, givenName, familyName, activationLink } }
        const data = response.data || {};
        
        // Map API response to RegistrationInvitation interface
        const invitation: RegistrationInvitation = {
          id: this.generateUniqueId(), // API doesn't provide ID, generate one
          email: data.email || email,
          givenName: data.givenName || givenName,
          familyName: data.familyName || familyName,
          invitationLink: data.activationLink || '', // The key field from API
          createdAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
          status: 'PENDING',
        };
        
        // Store in mock for backward compatibility
        this.mockInvitations.set(invitation.id, invitation);
        
        return invitation;
      }),
      catchError(error => {
        console.error('[API] Error:', error);
        throw error;
      })
    );
  }

  /**
   * @deprecated No longer needed - use createAndSendInvitation() instead
   * The unified API now handles both creation and email sending
   */
  sendInvitationEmail(email: string, invitationLink: string): Observable<{ success: boolean; message: string }> {
    console.warn('[DEPRECATED] sendInvitationEmail is deprecated. Use createAndSendInvitation() instead.');
    return of({ 
      success: true, 
      message: `Invito inviato con successo a ${email}` 
    }).pipe(delay(800));
  }

  /**
   * Get registration status for current logged-in professional
   * TODO: Replace with real API call like GET /api/v1/professionals/me/registration-status
   */
  getRegistrationStatus(userEmail: string): Observable<ProfessionalRegistrationStatus> {
    // Check if user has a registration status in mock storage
    let status = this.mockRegistrationStatus.get(userEmail);
    
    if (!status) {
      // Default: new user hasn't completed pre-registration
      status = {
        hasCompletedPreRegistration: false,
        currentStep: 'PORTAL_ACCESS',
      };
    }

    // Simulate API delay
    return of(status).pipe(delay(300));
  }

  /**
   * Mark pre-registration as completed for a user
   * This would be called after successful pre-registration submission
   */
  markPreRegistrationCompleted(userEmail: string, professionalId: string): void {
    const status: ProfessionalRegistrationStatus = {
      hasCompletedPreRegistration: true,
      professionalId: professionalId,
      currentStep: 'PENDING_DATA_INPUT',
    };
    
    this.mockRegistrationStatus.set(userEmail, status);
    console.log(`[MOCK] Pre-registration marked as completed for: ${userEmail}`);
  }

  /**
   * Update registration step
   */
  updateRegistrationStep(
    userEmail: string, 
    step: 'PORTAL_ACCESS' | 'PENDING_DATA_INPUT' | 'PENDING_VERIFICATION' | 'PENDING_CONTRACT' | 'PENDING_SIGNATURE'
  ): void {
    const status = this.mockRegistrationStatus.get(userEmail);
    if (status) {
      status.currentStep = step;
      this.mockRegistrationStatus.set(userEmail, status);
    }
  }

  /**
   * Get all invitations (for admin view)
   */
  getInvitations(): Observable<RegistrationInvitation[]> {
    return of(Array.from(this.mockInvitations.values())).pipe(delay(300));
  }

  /**
   * Generate unique ID (mock implementation)
   */
  private generateUniqueId(): string {
    return `inv_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Mock CIAM authentication callback
   * Simulates what happens when user returns from CIAM
   */
  handleCIAMCallback(token: string, userEmail: string): Observable<{ success: boolean; redirectUrl: string }> {
    console.log(`[MOCK CIAM] User authenticated: ${userEmail}`);
    console.log(`[MOCK CIAM] Token: ${token}`);

    // Store mock auth data
    sessionStorage.setItem('ciamToken', token);
    sessionStorage.setItem('userEmail', userEmail);

    // Check registration status and determine redirect
    const status = this.mockRegistrationStatus.get(userEmail);
    
    let redirectUrl = '/axa/pre-registration';
    
    if (status?.hasCompletedPreRegistration) {
      redirectUrl = '/axa/professionals-registration-progress';
    }

    return of({ success: true, redirectUrl }).pipe(delay(500));
  }
}
